import { AddCircleOutline } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getItemDisplayLabelForCount } from 'entities/CatalogItem';
import { getSelectedCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { getUserCanEditCatalogCategories } from 'entities/User/model/selectors/userPermissionsSeletors';
import { CategoryForm } from 'features/CategoryForm';
import { CategoryFormMode } from 'features/CategoryForm/module/types/categoryFormsTypes';

interface AddPropertyCatalogCategoryAtListProps {
    subtitleOverride?: string;
}

export const AddPropertyCatalogCategoryAtList = memo(
    ({ subtitleOverride }: AddPropertyCatalogCategoryAtListProps) => {
        const { t } = useTranslation();
        const [isEditMode, setIsEditMode] = useState<boolean>(false);
        const catalogId = useSelector(getSelectedPropertyCatalogId);
        const selectedItems = useSelector(getSelectedCatalogItems);
        const canEditCategories = useSelector(getUserCanEditCatalogCategories);

        const selectionSubtitle = useMemo(
            () =>
                subtitleOverride ||
                getItemDisplayLabelForCount(Object.values(selectedItems), t),
            [selectedItems, subtitleOverride, t],
        );

        const openInput = useCallback(() => {
            setIsEditMode(true);
        }, []);

        const closeInput = useCallback(() => {
            setIsEditMode(false);
        }, []);

        if (!canEditCategories) return null;

        return (
            <Box>
                <Box
                    alignItems="center"
                    sx={{ cursor: 'pointer' }}
                    onClick={openInput}
                >
                    <Button
                        data-testid="add-new-category-button"
                        sx={{ padding: 0 }}
                    >
                        <AddCircleOutline
                            color="primary"
                            sx={{ width: '24px', height: '24px' }}
                        />
                        <Typography
                            color="primary"
                            ml="8px"
                            typography="openSans.body2"
                        >
                            {t('Add new category')}
                        </Typography>
                    </Button>
                </Box>

                {isEditMode && (
                    <CategoryForm
                        mode={CategoryFormMode.CREATE}
                        catalogId={catalogId}
                        onClose={closeInput}
                        formTitle={t('New Category')}
                        selectionSubtitle={selectionSubtitle || undefined}
                    />
                )}
            </Box>
        );
    },
);
